import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { settingsService } from "../services/settingsService";
import {
  Farm,
  FarmAccessControl,
  FarmAccessAssignment,
  FarmAccessRole,
  FarmAccessGrant,
  FarmAccessScopeType,
  User,
} from "../types/settingsTypes";
import { useAuthContext } from "../../../contexts/AuthContext";

type RoleFormState = {
  roleId: string;
  name: string;
  description: string;
  domains: string[];
  actions: string[];
  scopeType: FarmAccessScopeType;
  scopeIds: string;
};

const ACTION_KEYS = ["read", "create", "update", "delete", "status.update"];
const DOMAIN_KEYS = [
  "farm",
  "identity",
  "animal",
  "lot",
  "movement",
  "gta",
  "pasture",
  "health",
  "reproduction",
  "milk",
  "nutrition",
  "genetics",
  "laboratory",
  "inventory",
  "workforce",
  "contracts",
  "marketplace",
  "certification",
  "traceability",
  "reports",
  "devices",
  "integration",
  "billing",
  "audit",
  "notifications",
  "documents",
];

const UserManagementView: React.FC = () => {
  const { t: tDash } = useTranslation("dashboard");
  const { authState } = useAuthContext();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState<string>("");
  const [accessControls, setAccessControls] = useState<FarmAccessControl[]>([]);
  const [isLoadingFarms, setIsLoadingFarms] = useState(false);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [roleForm, setRoleForm] = useState<RoleFormState>({
    roleId: "",
    name: "",
    description: "",
    domains: [],
    actions: [],
    scopeType: "global",
    scopeIds: "",
  });
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editingAccessKey, setEditingAccessKey] = useState<string | null>(null);

  const [userSearchEmail, setUserSearchEmail] = useState("");
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [invitedAccessControls, setInvitedAccessControls] = useState<Record<string, FarmAccessControl[]>>({});
  const [assignedUsersMap, setAssignedUsersMap] = useState<Record<string, User>>({});
  const [isLoadingAssignedUsers, setIsLoadingAssignedUsers] = useState(false);
  const [pendingAssignments, setPendingAssignments] = useState<string[]>([]);

  const userId = authState.profileId || authState.user?.id || "";

  const managerRole = useMemo<FarmAccessRole>(() => {
    return {
      role_id: "manager",
      name: tDash("settings.organization.access.roles.managerName"),
      description: tDash("settings.organization.access.roles.managerDescription"),
      grants: DOMAIN_KEYS.map((domain) => ({
        domain,
        actions: ACTION_KEYS,
        scope: { type: "global" },
      })),
    };
  }, [tDash]);

  useEffect(() => {
    const loadFarms = async () => {
      setIsLoadingFarms(true);
      setError(null);
      const response = await settingsService.getFarms(authState.profileId || undefined);
      if (!response.success) {
        setError(response.error || tDash("settings.organization.access.errors.loadFarms"));
        setFarms([]);
        setIsLoadingFarms(false);
        return;
      }
      setFarms(response.data);
      const ownedFarms = response.data.filter((farm) => farm.owner_id === userId);
      if (ownedFarms.length > 0) {
        setSelectedFarmId(ownedFarms[0]._key);
      } else {
        setSelectedFarmId("");
      }
      setIsLoadingFarms(false);
    };
    loadFarms();
  }, [authState.profileId, tDash, userId]);

  useEffect(() => {
    const loadAccessControl = async () => {
      if (!selectedFarmId) {
        setAccessControls([]);
        return;
      }
      setIsLoadingAccess(true);
      setError(null);
      const response = await settingsService.searchFarmAccessControl(selectedFarmId);
      if (!response.success) {
        setError(response.error || tDash("settings.organization.access.errors.loadAccess"));
        setAccessControls([]);
        setIsLoadingAccess(false);
        return;
      }
      setAccessControls(response.data);
      setIsLoadingAccess(false);
    };
    loadAccessControl();
  }, [selectedFarmId, tDash]);

  const openCreateRole = () => {
    setEditingRoleId(null);
    setEditingAccessKey(null);
    setPendingAssignments([]);
    setRoleForm({
      roleId: "",
      name: "",
      description: "",
      domains: DOMAIN_KEYS,
      actions: ACTION_KEYS,
      scopeType: "global",
      scopeIds: "",
    });
    setIsRoleDialogOpen(true);
  };

  const openEditRole = (role: FarmAccessRole, accessKey: string) => {
    const domains = role.grants.map((grant) => grant.domain);
    const actions = Array.from(new Set(role.grants.flatMap((grant) => grant.actions)));
    const scopeType = role.grants[0]?.scope?.type || "global";
    const scopeIds = (role.grants[0]?.scope?.ids || []).join(", ");
    setEditingRoleId(role.role_id);
    setEditingAccessKey(accessKey);
    setRoleForm({
      roleId: role.role_id,
      name: role.name,
      description: role.description || "",
      domains,
      actions,
      scopeType,
      scopeIds,
    });
    setIsRoleDialogOpen(true);
  };

  const handleOpenRoleModal = () => {
    openCreateRole();
  };

  const generateRoleId = (name: string) => {
    const base = name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
    const suffix = Date.now();
    return base ? `${base}_${suffix}` : `role_${suffix}`;
  };

  const handleSaveRole = async () => {
    if (!selectedFarmId) {
      setError(tDash("settings.organization.access.errors.noFarmSelected"));
      return;
    }
    if (!roleForm.name || roleForm.actions.length === 0) {
      setError(tDash("settings.organization.access.errors.invalidRole"));
      return;
    }
    setIsSaving(true);
    setError(null);
    const scopeIds = roleForm.scopeIds
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    const grants: FarmAccessGrant[] = DOMAIN_KEYS.map((domain) => ({
      domain,
      actions: roleForm.actions,
      scope: {
        type: roleForm.scopeType,
        ids: scopeIds.length > 0 ? scopeIds : undefined,
      },
    }));
    if (editingRoleId && editingAccessKey) {
      const activeAccessControl = accessControls.find((control) => control._key === editingAccessKey);
      if (!activeAccessControl) {
        setError(tDash("settings.organization.access.errors.saveRole"));
        setIsSaving(false);
        return;
      }
      const updatedRole: FarmAccessRole = {
        role_id: editingRoleId,
        name: roleForm.name,
        description: roleForm.description || undefined,
        grants,
      };
      const roles = activeAccessControl.roles.filter((role) => role.role_id !== editingRoleId);
      roles.push(updatedRole);
      const response = await settingsService.updateFarmAccessControl(editingAccessKey, {
        roles,
        updated_at: new Date().toISOString(),
        updated_by: userId || undefined,
      });
      if (!response.success) {
        setError(response.error || tDash("settings.organization.access.errors.saveRole"));
        setIsSaving(false);
        return;
      }
      setAccessControls((prev) =>
        prev.map((control) =>
          control._key === editingAccessKey ? { ...control, roles } : control
        )
      );
      setIsSaving(false);
      setIsRoleDialogOpen(false);
      return;
    }
    const roleId = roleForm.roleId || generateRoleId(roleForm.name);
    const newRole: FarmAccessRole = {
      role_id: roleId,
      name: roleForm.name,
      description: roleForm.description || undefined,
      grants,
    };
    const now = new Date().toISOString();
    const assignments = Array.from(new Set(pendingAssignments)).map((userId) => ({
      user_id: userId,
      role_id: roleId,
    }));
    const payload: FarmAccessControl = {
      farm_id: selectedFarmId,
      roles: [newRole],
      assignments,
      created_at: now,
      updated_at: now,
      created_by: userId || undefined,
      updated_by: userId || undefined,
    };
    const createResponse = await settingsService.createFarmAccessControl(payload);
    if (!createResponse.success) {
      setError(createResponse.error || tDash("settings.organization.access.errors.createAccess"));
      setIsSaving(false);
      return;
    }
    setAccessControls((prev) => [...prev, createResponse.data || payload]);
    setPendingAssignments([]);
    setSelectedUser(null);
    setIsSaving(false);
    setIsRoleDialogOpen(false);
  };

  const handleRemoveRole = async (accessKey: string, roleId: string) => {
    if (!accessKey || roleId === "manager") return;
    const activeAccessControl = accessControls.find((control) => control._key === accessKey);
    if (!activeAccessControl) return;
    setIsSaving(true);
    setError(null);
    const roles = activeAccessControl.roles.filter((role) => role.role_id !== roleId);
    const assignments = (activeAccessControl.assignments || []).filter(
      (assignment) => assignment.role_id !== roleId
    );
    const response = await settingsService.updateFarmAccessControl(accessKey, {
      roles,
      assignments,
      updated_at: new Date().toISOString(),
      updated_by: userId || undefined,
    });
    if (!response.success) {
      setError(response.error || tDash("settings.organization.access.errors.removeRole"));
      setIsSaving(false);
      return;
    }
    setAccessControls((prev) => {
      if (roles.length === 0) {
        return prev.filter((control) => control._key !== accessKey);
      }
      return prev.map((control) =>
        control._key === accessKey ? { ...control, roles, assignments } : control
      );
    });
    setIsSaving(false);
  };

  useEffect(() => {
    const query = userSearchEmail.trim();
    if (!query) {
      setUserSearchResults([]);
      setIsSearchingUsers(false);
      return;
    }
    setIsSearchingUsers(true);
    const timeoutId = setTimeout(async () => {
      const response = await settingsService.searchUsersByEmail(query);
      if (!response.success) {
        setError(response.error || tDash("settings.organization.access.errors.searchUsers"));
        setUserSearchResults([]);
        setIsSearchingUsers(false);
        return;
      }
      setUserSearchResults(response.data);
      setIsSearchingUsers(false);
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [userSearchEmail, tDash]);

  const handleAssignUserToRole = async () => {
    if (!selectedFarmId) {
      setError(tDash("settings.organization.access.errors.noFarmSelected"));
      return;
    }
    if (!selectedUser?._key) return;
    if (!roleForm.name || roleForm.actions.length === 0) {
      setError(tDash("settings.organization.access.errors.invalidRole"));
      return;
    }
    if (!editingRoleId || !editingAccessKey) {
      const nextRoleId = roleForm.roleId || generateRoleId(roleForm.name);
      if (!roleForm.roleId) {
        setRoleForm((prev) => ({ ...prev, roleId: nextRoleId }));
      }
      setPendingAssignments((prev) =>
        prev.includes(selectedUser._key) ? prev : [...prev, selectedUser._key]
      );
      setAssignedUsersMap((prev) => ({
        ...prev,
        [selectedUser._key]: selectedUser,
      }));
      setSelectedUser(null);
      return;
    }
    const activeAccessControl = accessControls.find((control) => control._key === editingAccessKey);
    if (!activeAccessControl) return;
    setIsSaving(true);
    setError(null);
    const currentAssignments = activeAccessControl.assignments || [];
    const assignments = currentAssignments.filter(
      (assignment) =>
        !(assignment.user_id === selectedUser._key && assignment.role_id === editingRoleId)
    );
    assignments.push({ user_id: selectedUser._key, role_id: editingRoleId });
    const response = await settingsService.updateFarmAccessControl(editingAccessKey, {
      assignments,
      updated_at: new Date().toISOString(),
      updated_by: userId || undefined,
    });
    if (!response.success) {
      setError(response.error || tDash("settings.organization.access.errors.assignUser"));
      setIsSaving(false);
      return;
    }
    setAccessControls((prev) =>
      prev.map((control) =>
        control._key === editingAccessKey ? { ...control, assignments } : control
      )
    );
    setSelectedUser(null);
    setIsSaving(false);
  };

  const handleRemoveUserFromRole = async (
    accessKey: string,
    roleId: string,
    targetUserId: string
  ) => {
    if (!accessKey) return;
    const activeAccessControl = accessControls.find((control) => control._key === accessKey);
    if (!activeAccessControl) return;
    setIsSaving(true);
    setError(null);
    const assignments = (activeAccessControl.assignments || []).filter(
      (assignment) => !(assignment.user_id === targetUserId && assignment.role_id === roleId)
    );
    const response = await settingsService.updateFarmAccessControl(accessKey, {
      assignments,
      updated_at: new Date().toISOString(),
      updated_by: userId || undefined,
    });
    if (!response.success) {
      setError(response.error || tDash("settings.organization.access.errors.assignUser"));
      setIsSaving(false);
      return;
    }
    setAccessControls((prev) =>
      prev.map((control) =>
        control._key === accessKey ? { ...control, assignments } : control
      )
    );
    const stillAssigned = assignments.some((assignment) => assignment.user_id === targetUserId);
    if (!stillAssigned) {
      setAssignedUsersMap((prev) => {
        const next = { ...prev };
        delete next[targetUserId];
        return next;
      });
    }
    setIsSaving(false);
  };

  const handleRemovePendingUser = (targetUserId: string) => {
    setPendingAssignments((prev) => prev.filter((id) => id !== targetUserId));
    const stillAssigned = accessControls.some((control) =>
      (control.assignments || []).some((assignment) => assignment.user_id === targetUserId)
    );
    if (!stillAssigned) {
      setAssignedUsersMap((prev) => {
        const next = { ...prev };
        delete next[targetUserId];
        return next;
      });
    }
  };

  const ownedFarms = useMemo(
    () => farms.filter((farm) => farm.owner_id === userId),
    [farms, userId]
  );
  const invitedFarms = useMemo(
    () =>
      farms.filter(
        (farm) => farm.owner_id !== userId && Array.isArray((farm as any).assigned_roles)
      ),
    [farms, userId]
  );
  const selectedFarm = ownedFarms.find((farm) => farm._key === selectedFarmId) || null;
  const hasOwnedFarms = ownedFarms.length > 0;
  const hasInvitedFarms = invitedFarms.length > 0;
  const showEmptyState = !isLoadingFarms && !hasOwnedFarms && !hasInvitedFarms;
  const activeAccessControl = useMemo(
    () => accessControls.find((control) => control._key === editingAccessKey) || null,
    [accessControls, editingAccessKey]
  );
  const roleEntries = useMemo(
    () =>
      accessControls.flatMap((control) =>
        control._key
          ? (control.roles || []).map((role) => ({
              role,
              accessKey: control._key || "",
            }))
          : []
      ),
    [accessControls]
  );
  const assignmentUserIds = useMemo(() => {
    const ids = accessControls.flatMap((control) =>
      (control.assignments || []).map((assignment) => assignment.user_id)
    );
    const combined = [...ids, ...pendingAssignments];
    return Array.from(new Set(combined)).filter(Boolean);
  }, [accessControls, pendingAssignments]);

  useEffect(() => {
    if (assignmentUserIds.length === 0) {
      setAssignedUsersMap({});
      return;
    }
    let active = true;
    const loadAssignedUsers = async () => {
      setIsLoadingAssignedUsers(true);
      const response = await settingsService.getUsersByIds(assignmentUserIds);
      if (!active) return;
      if (!response.success) {
        setAssignedUsersMap({});
        setIsLoadingAssignedUsers(false);
        return;
      }
      const next = response.data.reduce<Record<string, User>>((acc, user) => {
        acc[user._key] = user;
        return acc;
      }, {});
      setAssignedUsersMap((prev) => ({
        ...prev,
        ...next,
      }));
      setIsLoadingAssignedUsers(false);
    };
    loadAssignedUsers();
    return () => {
      active = false;
    };
  }, [assignmentUserIds]);

  useEffect(() => {
    if (invitedFarms.length === 0) {
      setInvitedAccessControls({});
      return;
    }
    let active = true;
    const loadInvitedAccess = async () => {
      const entries = await Promise.all(
        invitedFarms.map(async (farm) => {
          const response = await settingsService.searchFarmAccessControl(farm._key);
          return [farm._key, response.success ? response.data : []] as const;
        })
      );
      if (!active) return;
      const next: Record<string, FarmAccessControl[]> = {};
      entries.forEach(([farmId, access]) => {
        next[farmId] = access;
      });
      setInvitedAccessControls(next);
    };
    loadInvitedAccess();
    return () => {
      active = false;
    };
  }, [invitedFarms]);

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {hasOwnedFarms && (
        <>
          <Typography variant="h4" sx={{ mb: 1 }}>
            {tDash("settings.organization.access.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            {tDash("settings.organization.access.subtitle")}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ maxWidth: 420 }}>
              <InputLabel id="farm-select-label">{tDash("settings.organization.selectFarm")}</InputLabel>
              <Select
                labelId="farm-select-label"
                value={selectedFarmId}
                label={tDash("settings.organization.selectFarm")}
                onChange={(event) => setSelectedFarmId(event.target.value)}
                disabled={isLoadingFarms}
              >
                {ownedFarms.map((farm) => (
                  <MenuItem key={farm._key} value={farm._key}>
                    {farm.name || farm._key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isLoadingFarms && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">{tDash("settings.organization.access.loadingFarms")}</Typography>
            </Box>
          )}

          {selectedFarmId && isLoadingAccess && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">{tDash("settings.organization.access.loadingAccess")}</Typography>
            </Box>
          )}

          {selectedFarmId && !isLoadingAccess && (
            <Stack spacing={3} sx={{ mt: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">
                    {tDash("settings.organization.access.roles.title")}
                  </Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenRoleModal}>
                    {tDash("settings.organization.access.roles.add")}
                  </Button>
                </Box>
                <Stack spacing={2}>
                  {roleEntries.map(({ role, accessKey }) => (
                    <Paper key={`${accessKey}-${role.role_id}`} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box>
                          <Typography variant="subtitle1">{role.name}</Typography>
                          {role.description && (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              {role.description}
                            </Typography>
                          )}
                          {!role.description && (
                            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                              {tDash("settings.organization.access.roles.fullAccessLabel")}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" onClick={() => openEditRole(role, accessKey)}>
                            {tDash("settings.organization.access.roles.edit")}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveRole(accessKey, role.role_id)}
                            disabled={role.role_id === "manager"}
                          >
                            {tDash("settings.organization.access.roles.remove")}
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            </Stack>
          )}
        </>
      )}

      {hasInvitedFarms && (
        <Box sx={{ mt: hasOwnedFarms ? 4 : 0 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {tDash("settings.organization.membership.title")}
          </Typography>
          <Stack spacing={2}>
            {invitedFarms.map((farm) => {
              const assignedRoles: string[] = Array.isArray((farm as any).assigned_roles)
                ? (farm as any).assigned_roles
                : [];
              const accessList = invitedAccessControls[farm._key] || [];
              const roleDetails = assignedRoles.map((roleId: string) => {
                const match = accessList.flatMap((control) => control.roles || []).find((role) => role.role_id === roleId);
                return {
                  role_id: roleId,
                  name: match?.name || roleId,
                  description: match?.description || "",
                };
              });
              return (
                <Paper key={farm._key} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1">{farm.name || farm._key}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    {tDash("settings.organization.membership.roleLabel")}
                  </Typography>
                  <Stack spacing={1}>
                    {roleDetails.map((role) => (
                      <Box key={role.role_id}>
                        <Typography variant="body2">{role.name}</Typography>
                        {role.description && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {role.description}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      {showEmptyState && (
        <Alert severity="info" sx={{ mt: 3 }}>
          {tDash("settings.organization.membership.empty")}
        </Alert>
      )}

      <Dialog open={isRoleDialogOpen} onClose={() => setIsRoleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRoleId
            ? tDash("settings.organization.access.roles.editTitle")
            : tDash("settings.organization.access.roles.createTitle")}
        </DialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={tDash("settings.organization.access.roles.roleName")}
              value={roleForm.name}
              onChange={(event) => setRoleForm({ ...roleForm, name: event.target.value })}
              sx={{ mt: 1 }}
            />
            <TextField
              label={tDash("settings.organization.access.roles.roleDescription")}
              value={roleForm.description}
              onChange={(event) => setRoleForm({ ...roleForm, description: event.target.value })}
            />
            <Divider />
            <Typography variant="subtitle2">
              {tDash("settings.organization.access.roles.actions")}
            </Typography>
            <FormGroup row>
              {ACTION_KEYS.map((action) => (
                <FormControlLabel
                  key={action}
                  control={
                    <Checkbox
                      checked={roleForm.actions.includes(action)}
                      onChange={(event) => {
                        const next = event.target.checked
                          ? [...roleForm.actions, action]
                          : roleForm.actions.filter((item) => item !== action);
                        setRoleForm({ ...roleForm, actions: next });
                      }}
                    />
                  }
                  label={tDash(`settings.organization.access.actions.${action.replace(".", "_")}`)}
                />
              ))}
            </FormGroup>
            {(editingRoleId || pendingAssignments.length > 0) && (
              <>
                <Divider />
                <Typography variant="subtitle2">
                  {tDash("settings.organization.access.roles.usersTitle")}
                </Typography>
                {isLoadingAssignedUsers && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {tDash("settings.organization.access.roles.usersLoading")}
                  </Typography>
                )}
                {!isLoadingAssignedUsers && (() => {
                  const roleAssignments: FarmAccessAssignment[] = editingRoleId
                    ? (activeAccessControl?.assignments || []).filter(
                        (assignment) => assignment.role_id === roleForm.roleId
                      )
                    : pendingAssignments.map((userId) => ({
                        user_id: userId,
                        role_id: roleForm.roleId || "",
                      }));
                  if (roleAssignments.length === 0) {
                    return (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {tDash("settings.organization.access.roles.usersEmpty")}
                      </Typography>
                    );
                  }
                  return (
                    <Box sx={{ maxHeight: 240, overflowY: "auto", pr: 1 }}>
                      <Stack spacing={1}>
                        {roleAssignments.map((assignment) => {
                          const user = assignedUsersMap[assignment.user_id];
                          const displayName = user?.username || user?.full_name || assignment.user_id;
                          const displayEmail = user?.email || assignment.user_id;
                          return (
                            <Box
                              key={`${assignment.role_id}-${assignment.user_id}`}
                              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                            >
                              <Box>
                                <Typography variant="body2">{displayName}</Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {displayEmail}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  editingRoleId
                                    ? handleRemoveUserFromRole(
                                        editingAccessKey || "",
                                        roleForm.roleId,
                                        assignment.user_id
                                      )
                                    : handleRemovePendingUser(assignment.user_id)
                                }
                                disabled={isSaving}
                                aria-label={tDash("settings.organization.access.roles.removeUserAria")}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  );
                })()}
              </>
            )}
            <>
              <Divider />
              <Typography variant="subtitle2">
                {tDash("settings.organization.access.roles.assignUsersTitle")}
              </Typography>
              <Autocomplete
                options={userSearchResults}
                getOptionLabel={(option) => option.email || option._key}
                value={selectedUser}
                onChange={(_, value) => setSelectedUser(value)}
                onInputChange={(_, value) => setUserSearchEmail(value)}
                loading={isSearchingUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tDash("settings.organization.access.roles.assignUsersSearchLabel")}
                  />
                )}
              />
              {selectedUser?.email && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {selectedUser.email}
                </Typography>
              )}
              {isSearchingUsers && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {tDash("settings.organization.access.roles.assignUsersSearching")}
                  </Typography>
                </Box>
              )}
              <Button
                variant="outlined"
                onClick={handleAssignUserToRole}
                disabled={!selectedUser || isSaving}
              >
                {tDash("settings.organization.access.roles.assignUsersAdd")}
              </Button>
            </>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRoleDialogOpen(false)}>
            {tDash("settings.organization.access.roles.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveRole} disabled={isSaving}>
            {tDash("settings.organization.access.roles.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementView;
